import { BodyPix } from "@tensorflow-models/body-pix";
import { JitsiTrackEffect } from "lib/lib-jitsi-meet";
import { action, observable } from "mobx";

import { LocalUserState } from "../../common";
import { getBodySegmentationMaskForCurrentFrame, loadBodyPix } from "../bodyPix";
import {
    drawAndBlurImageOnOffScreenCanvas,
    flipCanvasHorizontal,
    renderImageDataToOffScreenCanvas,
    withLocalContext,
} from "../canvas";

export enum Effects {
    virtualBackground = "virtualBackground",
    blurBackground = "blurBackground",
    audioLevel = "audioLevel",
}

/** @deprecated */
export default class LocalVideoEffect implements JitsiTrackEffect {
    @observable public enabledEffects: Set<keyof typeof Effects> = new Set();
    @observable public bodyPix?: BodyPix;
    @observable public isStarted = false;
    @observable public backgroundImage?: HTMLImageElement;

    public isEffectEnabled(name: keyof typeof Effects): boolean {
        return this.enabledEffects.has(name);
    }

    @action
    public toggleEffect(name: keyof typeof Effects) {
        if (this.enabledEffects.has(name)) {
            this.enabledEffects.delete(name);
        } else {
            this.enabledEffects.add(name);
        }
    }

    @action
    public setBackgroundImage(image: HTMLImageElement) {
        this.backgroundImage = image;
    }

    public constructor(
        private video: HTMLVideoElement,
        private canvas: HTMLCanvasElement,
        private localUser: LocalUserState
    ) {
        loadBodyPix().then((net) => (this.bodyPix = net));
        this.enabledEffects.add(Effects.audioLevel);
    }

    private frameId?: number;

    // we can only enable the effect if our canvas is ready
    public isEnabled = () => this.canvas != null;

    // pipe webcam to canvas, apply effects, generate a new stream from capture
    public startEffect = (stream: MediaStream) => {
        const ctx = this.canvas?.getContext("2d");
        if (ctx == null) {
            console.error("Unable to process video effect, canvas ctx unavailable");
            return this.video.srcObject;
            // return this.originalVideoStream;
        } else if (this.video == null) {
            console.error("Unable to process video effect, self video unavailable");
            // return this.video.srcObject;
            // return this.originalVideoStream;
        }

        // TODO: do we need this?
        const firstVideoTrack = stream.getVideoTracks()[0];
        const { height, width } = firstVideoTrack.getSettings
            ? firstVideoTrack.getSettings()
            : firstVideoTrack.getConstraints();

        // @ts-ignore
        this.canvas.width = parseInt(width, 10);
        // @ts-ignore
        this.canvas.height = parseInt(height, 10);
        // @ts-ignore
        this.video.width = parseInt(width, 10);
        // @ts-ignore
        this.video.height = parseInt(height, 10);

        this.frameId = window.requestAnimationFrame(this.canvasDrawLoop);

        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/captureStream
        const newStream = (this.canvas as any).captureStream(30);
        this.isStarted = true;

        return newStream;
    };

    // swap back to webcam video
    public stopEffect = () => {
        this.isStarted = false;
        window.cancelAnimationFrame(this.frameId!);
    };

    private canvasDrawLoop = async () => {
        if (this.canvas != null && this.video != null) {
            const { width, height } = this.canvas;

            if (
                this.localUser.isSelfVideoDataLoaded &&
                this.enabledEffects.has(Effects.virtualBackground) &&
                this.bodyPix !== undefined
            ) {
                if (this.backgroundImage !== undefined) {
                    withLocalContext(this.canvas, (ctx) => {
                        // const { width: imgWidth, height: imgHeight } = this.backgroundImage!;
                        // ctx.scale(width / imgWidth, height / imgHeight);
                        ctx.globalCompositeOperation = "destination-over";
                        ctx.drawImage(this.backgroundImage!, 0, 0, width, height);
                    });
                }

                try {
                    const mask = await getBodySegmentationMaskForCurrentFrame(this.bodyPix, this.video);
                    renderVirtualBackgroundMask(this.canvas, this.video, mask);
                } catch (e) {
                    console.error(e);
                }
            } else {
                withLocalContext(this.canvas, (ctx) => {
                    flipCanvasHorizontal(this.canvas);
                    ctx.drawImage(this.video, 0, 0, width, height);
                });
            }

            if (this.enabledEffects.has(Effects.audioLevel)) {
                withLocalContext(this.canvas, (ctx) => {
                    // green
                    // ctx.fillStyle = "rgba(15, 153, 96, 0.2)";
                    ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
                    ctx.fillRect(0, 0, this.localUser.audioInputMeter.level * 1.5 * width, height);
                });
            }

            this.frameId = window.requestAnimationFrame(this.canvasDrawLoop);
        }
    };
}

function renderVirtualBackgroundMask(canvas: HTMLCanvasElement, video: HTMLVideoElement, mask: ImageData) {
    const maskOpacity = 1.0;
    const maskBlurAmount = 2;
    const flipHorizontal = true;

    // TODO: use offscreen canvas
    withLocalContext(canvas, (ctx: CanvasRenderingContext2D) => {
        if (flipHorizontal) {
            flipCanvasHorizontal(canvas);
        }
        const widthRatio = video.width / video.videoWidth;
        const heightRatio = video.height / video.videoHeight;
        ctx.scale(widthRatio, heightRatio);
        ctx.drawImage(video, 0, 0);
    });

    // draw mask
    withLocalContext(canvas, (ctx: CanvasRenderingContext2D) => {
        if (flipHorizontal) {
            flipCanvasHorizontal(canvas);
        }
        ctx.globalAlpha = maskOpacity;
        // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
        ctx.globalCompositeOperation = "destination-atop";
        const maskImage = renderImageDataToOffScreenCanvas(mask);
        const blurredMask = drawAndBlurImageOnOffScreenCanvas(maskImage, maskBlurAmount);
        ctx.drawImage(blurredMask, 0, 0, canvas.width, canvas.height);
    });
}
